import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Save, Building2, FileCheck2, MapPin, Stethoscope, AlertTriangle,
  CheckCircle2, Ban, Clock, ShieldCheck, ClipboardList, HeartHandshake, Trash2,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import FormInput from '../../components/FormInput';
import FileUpload from '../../components/FileUpload';
import LoadingSpinner from '../../components/LoadingSpinner';
import OnboardingProgress from '../../components/OnboardingProgress';
import * as vendorApi from '../../api/vendor.api';
import { errorMessage } from '../../api/client';

// VENDOR self-service onboarding form.
// Expanded 2026-08-28 to cover every field from the Clinic Onboarding
// Documents PDF (MOH/CKAPS, SSM, premise, doctor NRIC, LCP, NSR,
// indemnity, waste-disposal + T&C acceptance).
// Fields marked with * are mandatory for approval — the server-side
// /vendor/onboarding-progress endpoint enforces this; the UI just
// nudges the clinic with the star.

const TC_VERSION = 'v1.0-2026-08-28';

// Maps the API's doc-kind slug to the sub-document key on clinicProfile.
// Kept module-scoped so both the upload handler and the preview helper
// use the same source of truth.
const DOC_KIND_TO_BLOCK = {
  moh_ckaps:           'mohCkapsDoc',
  ssm:                 'ssmDoc',
  premise:             'premiseDoc',
  doctor_nric:         'doctorNricDoc',
  lcp:                 'lcpDoc',
  nsr:                 'nsrDoc',
  indemnity_insurance: 'indemnityInsuranceDoc',
  waste_disposal:      'wasteDisposalDoc',
};

const STATUS_PRESENTATION = {
  PENDING:   { tone: 'warning', icon: Clock,         title: 'Pending review',  body: "Admin will review your application — we'll email you the moment it's approved." },
  APPROVED:  { tone: 'success', icon: CheckCircle2,  title: 'Approved',        body: 'Your clinic is live and can receive bookings.' },
  REJECTED:  { tone: 'danger',  icon: Ban,           title: 'Action required', body: '' /* shows rejection reason inline */ },
  SUSPENDED: { tone: 'danger',  icon: AlertTriangle, title: 'Suspended',       body: 'Your clinic is temporarily off the platform. Contact admin for details.' },
};

// ★ marker for mandatory field labels.
function Req({ children }) {
  return (
    <>
      {children}{' '}
      <span style={{ color: '#dc2626', fontWeight: 700 }} title="Required for approval">*</span>
    </>
  );
}

// Status chip beside each document row — Uploaded (green) / Missing (grey)
// / Missing (red) when required-and-empty.
function DocStatus({ present, required }) {
  if (present) {
    return <span className="badge success" style={{ marginLeft: 8 }}>Uploaded</span>;
  }
  return (
    <span
      className="badge"
      style={{
        marginLeft: 8,
        background: required ? '#fee2e2' : '#f1f5f9',
        color: required ? '#991b1b' : '#475569',
      }}
    >
      Missing{required ? ' *' : ''}
    </span>
  );
}

function ClinicProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [profile, setProfile] = useState(null);

  const [form, setForm] = useState({
    // Identity
    clinicName: '', registrationNumber: '', clinicType: 'GP', specialty: '',
    // Facility
    mohCkapsNumber: '', ssmNumber: '', operatingHoursText: '',
    // Practitioner (PIC)
    mmcLicenseNumber: '', apcCertNumber: '', apcExpiryDate: '',
    prescribingDoctor: '', doctorFullName: '', doctorNric: '',
    // Conditional
    lcpNumber: '', nsrNumber: '',
    // Address
    addressLine1: '', addressLine2: '', city: '', state: '', postcode: '',
    locationLng: '', locationLat: '',
    // T&C
    tcAcceptedNow: false,
  });

  useEffect(() => {
    let cancelled = false;
    vendorApi.getMyClinicProfile()
      .then((data) => {
        if (cancelled) return;
        setProfile(data);
        const cp = data?.clinicProfile || {};
        setForm({
          clinicName: cp.clinicName || '',
          registrationNumber: cp.registrationNumber || '',
          clinicType: cp.clinicType || 'GP',
          specialty: cp.specialty || '',
          mohCkapsNumber: cp.mohCkapsNumber || '',
          ssmNumber: cp.ssmNumber || '',
          operatingHoursText: cp.operatingHoursText || '',
          mmcLicenseNumber: cp.mmcLicenseNumber || '',
          apcCertNumber: cp.apcCertNumber || '',
          apcExpiryDate: cp.apcExpiryDate ? new Date(cp.apcExpiryDate).toISOString().slice(0, 10) : '',
          prescribingDoctor: cp.prescribingDoctor || '',
          doctorFullName: cp.doctorFullName || '',
          doctorNric: cp.doctorNric || '',
          lcpNumber: cp.lcpNumber || '',
          nsrNumber: cp.nsrNumber || '',
          addressLine1: cp.addressLine1 || '',
          addressLine2: cp.addressLine2 || '',
          city: cp.city || '',
          state: cp.state || '',
          postcode: cp.postcode || '',
          locationLng: cp.location?.coordinates?.[0] ?? '',
          locationLat: cp.location?.coordinates?.[1] ?? '',
          tcAcceptedNow: false,
        });
      })
      .catch((e) => toast.error(errorMessage(e)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  function set(patch) { setForm((f) => ({ ...f, ...patch })); }

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        clinicName: form.clinicName?.trim(),
        registrationNumber: form.registrationNumber?.trim(),
        clinicType: form.clinicType,
        specialty: form.specialty?.trim(),
        mohCkapsNumber: form.mohCkapsNumber?.trim(),
        ssmNumber: form.ssmNumber?.trim(),
        operatingHoursText: form.operatingHoursText?.trim(),
        mmcLicenseNumber: form.mmcLicenseNumber?.trim(),
        apcCertNumber: form.apcCertNumber?.trim(),
        prescribingDoctor: form.prescribingDoctor?.trim(),
        doctorFullName: form.doctorFullName?.trim(),
        doctorNric: form.doctorNric?.trim().replace(/\s+/g, ''),
        lcpNumber: form.lcpNumber?.trim(),
        nsrNumber: form.nsrNumber?.trim(),
        addressLine1: form.addressLine1?.trim(),
        addressLine2: form.addressLine2?.trim(),
        city: form.city?.trim(),
        state: form.state?.trim(),
        postcode: form.postcode?.trim(),
      };
      if (form.apcExpiryDate) payload.apcExpiryDate = new Date(form.apcExpiryDate).toISOString();
      if (form.locationLng !== '' && form.locationLat !== '') {
        payload.location = { lng: Number(form.locationLng), lat: Number(form.locationLat) };
      }
      // Client 2026-09-10: submitting the form IS the T&C acceptance —
      // checkbox was removed. Stamp acceptance on the first submit for
      // clinics that haven't accepted yet; on subsequent saves the
      // backend keeps the original `tcAcceptedAt` timestamp.
      if (!tcAlreadyAccepted) {
        payload.tcAccepted = true;
        payload.tcVersion  = TC_VERSION;
      }
      const updated = await vendorApi.updateMyClinicProfile(payload);
      setProfile((p) => ({ ...(p || {}), clinicProfile: updated.clinicProfile }));
      toast.success(tcAlreadyAccepted ? 'Saved' : 'Application submitted');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleUpload(kind, file) {
    if (!file) return;
    try {
      const res = await vendorApi.uploadClinicDocument(kind, file);
      setProfile((p) => {
        const cp = { ...(p?.clinicProfile || {}) };
        // Legacy kinds write to the flat fields; new kinds write to a
        // `<kind>Doc` sub-object. Mirror the server behaviour here so the
        // UI reflects the upload without a page reload.
        if (kind === 'mmc') { cp.mmcLicenseDocKey = 'set'; cp.mmcLicenseDocUrl = res.fileUrl; }
        else if (kind === 'apc') { cp.apcCertDocKey = 'set'; cp.apcCertDocUrl = res.fileUrl; }
        else {
          const blockField = DOC_KIND_TO_BLOCK[kind];
          if (blockField) {
            cp[blockField] = {
              key:          'set',
              url:          res.fileUrl,
              uploadedAt:   new Date().toISOString(),
              originalName: res.fileName || file.name,
              fileSize:     res.fileSize || file.size,
              fileType:     res.fileType || file.type,
            };
          }
        }
        if (cp.approvalStatus === 'REJECTED') cp.approvalStatus = 'PENDING';
        return { ...p, clinicProfile: cp };
      });
      toast.success(`${kind.replace(/_/g, ' ').toUpperCase()} document uploaded`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  // Client 2026-09-10: open the currently-uploaded doc so the clinic can
  // check what they submitted. Uses a blob download so the JWT header is
  // included (a plain <a href> wouldn't authenticate).
  async function handleViewDocument(kind) {
    try {
      const blob = await vendorApi.fetchMyClinicDocumentBlob(kind);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  // Extract the existing-file metadata for the FileUpload preview strip.
  // Returns null when nothing is uploaded yet so the strip stays hidden.
  function existingFor(kind) {
    const cp = profile?.clinicProfile || {};
    if (kind === 'mmc') {
      return cp.mmcLicenseDocKey
        ? { name: 'Uploaded MMC document', size: 0, uploadedAt: null }
        : null;
    }
    if (kind === 'apc') {
      return cp.apcCertDocKey
        ? { name: 'Uploaded APC certificate', size: 0, uploadedAt: null }
        : null;
    }
    const block = cp[DOC_KIND_TO_BLOCK[kind]];
    if (!block?.key) return null;
    return {
      name:       block.originalName || `Uploaded ${kind.replace(/_/g, ' ')}`,
      size:       block.fileSize || 0,
      type:       block.fileType || '',
      uploadedAt: block.uploadedAt || null,
    };
  }

  if (loading) return <LoadingSpinner />;

  const cp = profile?.clinicProfile || {};
  const status = cp.approvalStatus || 'PENDING';
  const pres = STATUS_PRESENTATION[status] || STATUS_PRESENTATION.PENDING;
  const StatusIcon = pres.icon;
  const apcExpired = cp.apcExpiryDate && new Date(cp.apcExpiryDate) < new Date();

  const isAesthetic  = form.clinicType === 'AESTHETIC';
  const isSpecialist = form.clinicType === 'SPECIALIST';

  const tcAlreadyAccepted = Boolean(cp.tcAcceptedAt);

  return (
    <>
      <PageHeader
        title="Clinic profile"
        subtitle="Provide your licensing, premise and doctor details. Fields marked * are required before our super-admin can approve your clinic."
      />

      <OnboardingProgress />

      {/* Status banner */}
      <div className={`notice ${pres.tone}`} style={{ marginBottom: 'var(--s-4)' }}>
        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <StatusIcon size={20} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div className="bold">{pres.title}</div>
            {status === 'REJECTED' ? (
              <div style={{ marginTop: 4 }}>
                <span className="muted">Reason: </span>{cp.rejectionReason || '—'}
                <div className="muted text-sm" style={{ marginTop: 4 }}>
                  Edit any field below or re-upload a document — your application returns to the queue automatically.
                </div>
              </div>
            ) : <div style={{ marginTop: 4 }}>{pres.body}</div>}
          </div>
        </div>
      </div>

      {/* Section 1 — Clinic identity */}
      <div className="card" data-tour="section-basics">
        <div className="card-title">
          <h3><Building2 size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Clinic basics</h3>
        </div>
        <div className="grid grid-2" style={{ gap: 'var(--s-3)' }}>
          <FormInput label={<Req>Clinic name</Req>} value={form.clinicName} onChange={(e) => set({ clinicName: e.target.value })} />
          <div className="field">
            <label><Req>Clinic type</Req></label>
            <select
              className="input"
              value={form.clinicType}
              onChange={(e) => set({ clinicType: e.target.value })}
            >
              <option value="GP">General Practice (GP)</option>
              <option value="AESTHETIC">Aesthetic clinic</option>
              <option value="SPECIALIST">Specialist clinic</option>
              <option value="DENTAL">Dental</option>
              <option value="OTHER">Other</option>
            </select>
            <div className="hint">Aesthetic clinics require an LCP; specialist clinics require NSR registration.</div>
          </div>
          {isSpecialist && (
            <FormInput
              label="Specialty"
              value={form.specialty}
              onChange={(e) => set({ specialty: e.target.value })}
              placeholder="e.g. Paediatric, Dermatology, O&G, ENT"
            />
          )}
        </div>
      </div>

      {/* Section 2 — Facility documents (MOH/CKAPS, SSM, premise, hours) */}
      <div className="card" style={{ marginTop: 'var(--s-4)' }} data-tour="section-facility">
        <div className="card-title">
          <h3><FileCheck2 size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Facility documents</h3>
          <span className="muted text-sm">Private Healthcare Facilities and Services Act 1998 (Act 586)</span>
        </div>

        <div className="grid grid-2" style={{ gap: 'var(--s-3)' }}>
          <FormInput
            label={<Req>MOH / CKAPS registration no.</Req>}
            value={form.mohCkapsNumber}
            onChange={(e) => set({ mohCkapsNumber: e.target.value })}
            hint="From Borang B / Borang C (Certificate of Registration - COR)."
          />
          <FormInput
            label={<Req>SSM registration no.</Req>}
            value={form.ssmNumber}
            onChange={(e) => set({ ssmNumber: e.target.value })}
            hint="Business registration (Form 9 / Section 14 & 17)."
          />
        </div>

        <div className="field" style={{ marginTop: 'var(--s-3)' }}>
          <label><Req>Clinic operating hours</Req></label>
          <textarea
            className="textarea"
            rows={2}
            value={form.operatingHoursText}
            onChange={(e) => set({ operatingHoursText: e.target.value })}
            placeholder="e.g. Mon–Fri 9am–6pm, Sat 9am–1pm. Closed Sun & public holidays."
          />
          <div className="hint">Detailed slot rules are set on the Booking Settings page.</div>
        </div>

        <div className="grid grid-3" style={{ gap: 'var(--s-4)', marginTop: 'var(--s-4)' }}>
          <div>
            <div className="bold" style={{ marginBottom: 6 }}>
              <Req>MOH / CKAPS certificate (Borang B / C)</Req>
              <DocStatus present={Boolean(cp.mohCkapsDoc?.key)} required />
            </div>
            <FileUpload onChange={(f) => handleUpload('moh_ckaps', f)} maxBytes={10 * 1024 * 1024}
              existing={existingFor('moh_ckaps')} onView={() => handleViewDocument('moh_ckaps')} />
          </div>
          <div>
            <div className="bold" style={{ marginBottom: 6 }}>
              <Req>SSM certificate</Req>
              <DocStatus present={Boolean(cp.ssmDoc?.key)} required />
            </div>
            <FileUpload onChange={(f) => handleUpload('ssm', f)} maxBytes={10 * 1024 * 1024}
              existing={existingFor('ssm')} onView={() => handleViewDocument('ssm')} />
          </div>
          <div>
            <div className="bold" style={{ marginBottom: 6 }}>
              <Req>Premise proof (utility bill / tenancy / PBT licence)</Req>
              <DocStatus present={Boolean(cp.premiseDoc?.key)} required />
            </div>
            <FileUpload onChange={(f) => handleUpload('premise', f)} maxBytes={10 * 1024 * 1024}
              existing={existingFor('premise')} onView={() => handleViewDocument('premise')} />
          </div>
        </div>
      </div>

      {/* Section 3 — Practitioner (PIC) */}
      <div className="card" style={{ marginTop: 'var(--s-4)' }} data-tour="section-practitioner">
        <div className="card-title">
          <h3><Stethoscope size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Practitioner (PIC) documents</h3>
          <span className="muted text-sm">Malaysian Medical Council · Medical Act 1971</span>
        </div>

        <div className="grid grid-2" style={{ gap: 'var(--s-3)' }}>
          <FormInput
            label={<Req>Doctor full name</Req>}
            value={form.doctorFullName}
            onChange={(e) => set({ doctorFullName: e.target.value })}
            hint="Must match MMC/APC registry."
          />
          <FormInput
            label={<Req>NRIC / Doctor ID</Req>}
            value={form.doctorNric}
            onChange={(e) => set({ doctorNric: e.target.value.replace(/[^\d-]/g, '') })}
            placeholder="e.g. 850101-14-1234"
          />
          <FormInput
            label={<Req>MMC registration no.</Req>}
            value={form.mmcLicenseNumber}
            onChange={(e) => set({ mmcLicenseNumber: e.target.value })}
            hint="MMC Full Registration Certificate (Sijil Pendaftaran Penuh)."
          />
          <FormInput
            label="Prescribing doctor (display name)"
            value={form.prescribingDoctor}
            onChange={(e) => set({ prescribingDoctor: e.target.value })}
            hint="Shown to patients — often same as the PIC."
          />
          <FormInput
            label={<Req>APC certificate no.</Req>}
            value={form.apcCertNumber}
            onChange={(e) => set({ apcCertNumber: e.target.value })}
          />
          <FormInput
            label={<Req>APC expiry date</Req>}
            type="date"
            value={form.apcExpiryDate}
            onChange={(e) => set({ apcExpiryDate: e.target.value })}
            hint={apcExpired ? 'Currently expired — admin cannot approve.' : 'Must be renewed yearly (expires 31 December).'}
            error={apcExpired ? 'APC expired' : undefined}
          />
        </div>

        <div className="grid grid-3" style={{ gap: 'var(--s-4)', marginTop: 'var(--s-4)' }}>
          <div>
            <div className="bold" style={{ marginBottom: 6 }}>
              <Req>MMC Full Registration Certificate</Req>
              <DocStatus present={Boolean(cp.mmcLicenseDocKey)} required />
            </div>
            <FileUpload onChange={(f) => handleUpload('mmc', f)} maxBytes={10 * 1024 * 1024}
              existing={existingFor('mmc')} onView={() => handleViewDocument('mmc')} />
          </div>
          <div>
            <div className="bold" style={{ marginBottom: 6 }}>
              <Req>Current-year APC document</Req>
              <DocStatus present={Boolean(cp.apcCertDocKey)} required />
            </div>
            <FileUpload onChange={(f) => handleUpload('apc', f)} maxBytes={10 * 1024 * 1024}
              existing={existingFor('apc')} onView={() => handleViewDocument('apc')} />
          </div>
          <div>
            <div className="bold" style={{ marginBottom: 6 }}>
              <Req>NRIC copy / Doctor ID</Req>
              <DocStatus present={Boolean(cp.doctorNricDoc?.key)} required />
            </div>
            <FileUpload onChange={(f) => handleUpload('doctor_nric', f)} maxBytes={10 * 1024 * 1024}
              existing={existingFor('doctor_nric')} onView={() => handleViewDocument('doctor_nric')} />
          </div>
        </div>
      </div>

      {/* Section 4 — Conditional specialty documents */}
      {(isAesthetic || isSpecialist) && (
        <div className="card" style={{ marginTop: 'var(--s-4)' }} data-tour="section-conditional">
          <div className="card-title">
            <h3><ClipboardList size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Specialty documents</h3>
            <span className="muted text-sm">
              {isAesthetic ? 'Aesthetic clinic — LCP required.' : 'Specialist clinic — NSR required.'}
            </span>
          </div>

          {isAesthetic && (
            <div className="grid grid-2" style={{ gap: 'var(--s-3)' }}>
              <FormInput
                label={<Req>LCP number</Req>}
                value={form.lcpNumber}
                onChange={(e) => set({ lcpNumber: e.target.value })}
                hint="Letter of Credentialing and Privileging (MOH-issued)."
              />
              <div>
                <div className="bold" style={{ marginBottom: 6 }}>
                  <Req>LCP certificate</Req>
                  <DocStatus present={Boolean(cp.lcpDoc?.key)} required />
                </div>
                <FileUpload onChange={(f) => handleUpload('lcp', f)} maxBytes={10 * 1024 * 1024}
                  existing={existingFor('lcp')} onView={() => handleViewDocument('lcp')} />
              </div>
            </div>
          )}

          {isSpecialist && (
            <div className="grid grid-2" style={{ gap: 'var(--s-3)', marginTop: isAesthetic ? 'var(--s-4)' : 0 }}>
              <FormInput
                label={<Req>NSR number</Req>}
                value={form.nsrNumber}
                onChange={(e) => set({ nsrNumber: e.target.value })}
                hint="National Specialist Register — specialty registration number."
              />
              <div>
                <div className="bold" style={{ marginBottom: 6 }}>
                  <Req>NSR certificate</Req>
                  <DocStatus present={Boolean(cp.nsrDoc?.key)} required />
                </div>
                <FileUpload onChange={(f) => handleUpload('nsr', f)} maxBytes={10 * 1024 * 1024}
                  existing={existingFor('nsr')} onView={() => handleViewDocument('nsr')} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section 5 — Recommended add-ons (optional) */}
      <div className="card" style={{ marginTop: 'var(--s-4)' }}>
        <div className="card-title">
          <h3><HeartHandshake size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Recommended (best practice)</h3>
          <span className="muted text-sm">Optional — strengthens your onboarding profile.</span>
        </div>
        <div className="grid grid-2" style={{ gap: 'var(--s-4)' }}>
          <div>
            <div className="bold" style={{ marginBottom: 6 }}>
              Professional Medical Indemnity Insurance
              <DocStatus present={Boolean(cp.indemnityInsuranceDoc?.key)} />
            </div>
            <FileUpload onChange={(f) => handleUpload('indemnity_insurance', f)} maxBytes={10 * 1024 * 1024}
              existing={existingFor('indemnity_insurance')} onView={() => handleViewDocument('indemnity_insurance')} />
            <div className="hint">e.g. MPS, Medilink — proof of active coverage.</div>
          </div>
          <div>
            <div className="bold" style={{ marginBottom: 6 }}>
              <Trash2 size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
              Clinical Waste Disposal Agreement
              <DocStatus present={Boolean(cp.wasteDisposalDoc?.key)} />
            </div>
            <FileUpload onChange={(f) => handleUpload('waste_disposal', f)} maxBytes={10 * 1024 * 1024}
              existing={existingFor('waste_disposal')} onView={() => handleViewDocument('waste_disposal')} />
            <div className="hint">Scheduled-waste compliance evidence.</div>
          </div>
        </div>
      </div>

      {/* Section 6 — Address & location */}
      <div className="card" style={{ marginTop: 'var(--s-4)' }}>
        <div className="card-title">
          <h3><MapPin size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Address & location</h3>
          <span className="muted text-sm">Coordinates power the matching engine — customers see closer clinics first.</span>
        </div>
        <div className="grid grid-2" style={{ gap: 'var(--s-3)' }}>
          <FormInput label={<Req>Address line 1</Req>} value={form.addressLine1} onChange={(e) => set({ addressLine1: e.target.value })} />
          <FormInput label="Address line 2" value={form.addressLine2} onChange={(e) => set({ addressLine2: e.target.value })} />
          <FormInput label={<Req>City</Req>} value={form.city} onChange={(e) => set({ city: e.target.value })} />
          <FormInput label={<Req>State</Req>} value={form.state} onChange={(e) => set({ state: e.target.value })} />
          <FormInput label={<Req>Postcode</Req>} value={form.postcode} onChange={(e) => set({ postcode: e.target.value })} />
          <div /> {/* spacer */}
          <FormInput label="Longitude" type="number" value={form.locationLng} onChange={(e) => set({ locationLng: e.target.value })} hint="e.g. 101.6869" />
          <FormInput label="Latitude" type="number" value={form.locationLat} onChange={(e) => set({ locationLat: e.target.value })} hint="e.g. 3.1390" />
        </div>
      </div>

      {/* Section 7 — Panel Terms (client 2026-09-10). Checkbox removed;
          submitting the application IS the confirmation. Copy simplified
          to the three key commitments plus the credentials line. */}
      <div className="card" style={{ marginTop: 'var(--s-4)' }} data-tour="section-tc">
        <div className="card-title">
          <h3><ShieldCheck size={16} style={{ verticalAlign: '-3px', marginRight: 8 }} /> Partner with MyDr.AI</h3>
        </div>

        <ul style={{ marginTop: 12, marginBottom: 12, paddingLeft: 20, lineHeight: 1.7, color: '#0f172a' }}>
          <li>
            <strong>Active Credentials:</strong> Valid SSM, MOH/CKAPS, MMC, and current APC.
          </li>
          <li>
            <strong>Physical Consults:</strong> In-person care and prescribing at your clinic.
          </li>
          <li>
            <strong>Fair Terms:</strong> Fees apply only to completed visits. Cancel anytime with 14 days&apos; notice.
          </li>
        </ul>

        {tcAlreadyAccepted && (
          <div className="notice success" style={{ marginTop: 8 }}>
            <CheckCircle2 size={14} style={{ verticalAlign: -1, marginRight: 6 }} />
            Application submitted on{' '}
            <strong>{new Date(cp.tcAcceptedAt).toLocaleDateString('en-MY', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
            {cp.tcAcceptedVersion ? <> (version <code>{cp.tcAcceptedVersion}</code>)</> : null}.
          </div>
        )}
      </div>

      <div className="col" style={{ marginTop: 'var(--s-4)', alignItems: 'flex-end', gap: 8 }}>
        <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
          <Save size={16} /> {saving
            ? 'Submitting…'
            : tcAlreadyAccepted
              ? 'Save changes'
              : 'Submit Application'}
        </button>
        {!tcAlreadyAccepted && (
          <div className="muted text-xs" style={{ textAlign: 'right', maxWidth: 460, fontStyle: 'italic' }}>
            Submitting confirms active credentials and adherence to our{' '}
            <Link to="/legal/terms" target="_blank" rel="noopener noreferrer">Panel Terms</Link>.
          </div>
        )}
      </div>
    </>
  );
}

export default ClinicProfile;
